import { Progress } from '@heidi/design-system/progress';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { File, Loader2 } from 'lucide-react';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';

import { skillFileUploadStatusMessages } from './SkillFileUploadStatus.messages';

export interface PendingSkillFileUpload {
  jobId: string;
  filename: string;
  phase: 'uploading' | 'processing';
  uploadProgress: number | null;
}

interface SkillFileUploadStatusProps {
  upload: PendingSkillFileUpload;
  uploadProgress: number | null;
  variant?: 'chip' | 'card';
  className?: string;
}

export function SkillFileUploadStatus({
  upload,
  uploadProgress,
  variant = 'card',
  className,
}: SkillFileUploadStatusProps): JSX.Element {
  const intl = useIntl();
  const isUploading = upload.phase === 'uploading';
  const byteProgress = isUploading ? (uploadProgress ?? upload.uploadProgress) : null;
  const statusMessage =
    isUploading && byteProgress !== null
      ? intl.formatMessage(skillFileUploadStatusMessages.uploadingWithProgress, {
          progress: byteProgress,
        })
      : intl.formatMessage(
          isUploading
            ? skillFileUploadStatusMessages.uploading
            : skillFileUploadStatusMessages.processing
        );

  if (variant === 'chip') {
    return (
      <div
        className={cn(
          'inline-flex max-w-full flex-col gap-1.5 rounded-full border border-dashed',
          'border-text-active/40 bg-background-secondary px-2.5 py-1.5 text-text-primary',
          className
        )}
        aria-live="polite"
        data-testid="skill-file-upload-chip"
      >
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <Loader2
            className="size-3 shrink-0 animate-spin text-text-active"
            aria-hidden
          />
          <File className="size-3 shrink-0 text-text-secondary" aria-hidden />
          {/* The phase suffix is the chip's point — only the filename may
              truncate, so the phase chunk stays outside the truncated span. */}
          <TypographyCaption className="flex min-w-0 items-center gap-1 text-text-tertiary">
            <FormattedMessage
              {...skillFileUploadStatusMessages.chipPhase}
              values={{
                filename: upload.filename,
                phase: upload.phase,
                filenameStyle: (chunks) => (
                  <span className="truncate text-text-primary">{chunks}</span>
                ),
                phaseStyle: (chunks) => (
                  <span className="shrink-0 whitespace-nowrap">{chunks}</span>
                ),
              }}
            />
          </TypographyCaption>
        </span>
        <SkillFileUploadProgress
          isUploading={isUploading}
          byteProgress={byteProgress}
          filename={upload.filename}
          compact
          intl={intl}
        />
        <TypographyCaption className="truncate text-text-tertiary">
          {statusMessage}
        </TypographyCaption>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-lg border border-border bg-background-secondary p-3',
        className
      )}
      aria-live="polite"
      data-testid="skill-file-upload-card"
    >
      <div className="flex items-start gap-2">
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface">
          <File className="size-4 text-text-secondary" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <TypographyP2SemiBold className="truncate text-text-primary">
            {upload.filename}
          </TypographyP2SemiBold>
          <TypographyCaption className="text-text-tertiary">
            {intl.formatMessage(skillFileUploadStatusMessages.cardPhase, {
              phase: upload.phase,
            })}
          </TypographyCaption>
        </div>
        <Loader2 className="size-4 shrink-0 animate-spin text-text-active" aria-hidden />
      </div>

      <SkillFileUploadProgress
        isUploading={isUploading}
        byteProgress={byteProgress}
        filename={upload.filename}
        intl={intl}
      />

      <TypographyCaption className="text-text-secondary">
        {statusMessage}
      </TypographyCaption>
    </div>
  );
}

function SkillFileUploadProgress({
  isUploading,
  byteProgress,
  filename,
  compact = false,
  intl,
}: {
  isUploading: boolean;
  byteProgress: number | null;
  filename: string;
  compact?: boolean;
  intl: IntlShape;
}): JSX.Element {
  if (isUploading && byteProgress !== null) {
    return (
      <Progress
        value={byteProgress}
        className={cn(compact ? 'h-1' : 'h-2')}
        aria-label={intl.formatMessage(skillFileUploadStatusMessages.uploadProgressAria, {
          filename,
        })}
      />
    );
  }

  return (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-full bg-surface',
        compact ? 'h-1' : 'h-2'
      )}
      role="progressbar"
      aria-label={intl.formatMessage(
        skillFileUploadStatusMessages.processingProgressAria,
        {
          filename,
        }
      )}
      aria-busy="true"
    >
      <div
        className={cn(
          'size-full animate-pulse rounded-full bg-text-active/70',
          compact ? 'min-h-1' : 'min-h-2'
        )}
      />
    </div>
  );
}
